import { HttpErrorResponse } from '@angular/common/http';
import { TimeoutError } from 'rxjs';

/** The WordPress REST namespace every super-form route lives under. */
export const API_NAMESPACE = '/wp-json/super-form/v1/';

/**
 * DEV-7386: console output is recorded as a Bugsnag breadcrumb, so a request URL
 * must never be logged whole. Three routes carry a per-lead capability token in
 * `?id=` (verify-otp, resubmit-phone-for-otp, resend-otp), vc-pre-items carries
 * the visitor's _vcToken as a path segment, and get-customer / validate-email
 * carry the lead's own details. Reduce any URL to its endpoint name: the one
 * part that has diagnostic value and holds no identifier.
 */
export function safeEndpoint( url: string | null | undefined ): string {

	if( ! url ) {

		return 'unknown';
	}

	const path = url.split( '?' )[0];
	const start = path.indexOf( API_NAMESPACE );

	// First segment only: anything after it is an identifier, e.g. vc-pre-items/<token>.
	if( start !== -1 ) {

		return path.substring( start + API_NAMESPACE.length ).split( '/' )[0] || 'unknown';
	}

	// A route passed without the namespace is already namespace-relative, so the same
	// rule applies to it: keep the first segment, drop the rest. Only an absolute URL
	// that misses the namespace (pro6pp, the ERP base, local assets) keeps its path -
	// nothing there is an identifier once the query string is gone.
	return path.includes( '://' ) ? path : path.split( '/' )[0] || 'unknown';
}

/**
 * Blank out the values of any query parameters embedded in free text. Browser and
 * RxJS error messages occasionally quote the request URL back at us.
 */
export function redactQueryValues( text: string ): string {

	return text.replace( /([?&][^=&\s]+=)[^&\s"'`]*/g, '$1[redacted]' );
}

/**
 * A short, identifier-free description of a failed request. Never log the error
 * itself: an HttpErrorResponse repeats the request URL in its `url`, and its
 * `error` body can echo back what was submitted.
 */
export function describeError( error: unknown ): string {

	if( error instanceof HttpErrorResponse ) {

		return safeEndpoint( error.url ) + ' returned status ' + error.status;
	}

	if( error instanceof Error ) {

		return redactQueryValues( error.message );
	}

	return 'non-http error';
}

/**
 * What went wrong with a request, in a form that holds no identifier: `timeout`,
 * `no response` (status 0 - the request never completed on the client) or `status <n>`.
 */
export function describeFailure( error: unknown ): string {

	if( error instanceof HttpErrorResponse ) {

		return error.status === 0 ? 'no response' : 'status ' + error.status;
	}

	if( error instanceof TimeoutError ) {

		return 'timeout';
	}

	return 'error';
}

/**
 * DEV-7562: what ApiService rejects with. It used to reject with the bare string
 * 'Something bad happened; please try again later.', so every failed request that a
 * caller left unhandled landed in Bugsnag under that one message, with no way to tell
 * which endpoint failed or how. The message carries the endpoint name and the failure
 * type only - pass the endpoint through safeEndpoint(), never a route or URL.
 */
export class ApiRequestError extends Error {

	public constructor( public readonly endpoint: string, public readonly failure: string ) {

		super( 'API request failed: ' + endpoint + ' (' + failure + ')' );
		this.name = 'ApiRequestError';
	}
}

/**
 * Blank out an identifier carried as a path segment. safeEndpoint() keeps only the
 * first segment after the namespace because everything past it is an identifier -
 * `vc-pre-items/<_vcToken>`. Free text quoting a URL back at us needs the same cut:
 * redactQueryValues() is anchored on `?`/`&` and never touches a path.
 *
 * Deliberately blunt: it also blanks the locale on `vc/<lang>`, the one route whose
 * second segment is not an identifier. safeEndpoint() already drops that from our own
 * log lines, and over-redacting one breadcrumb is the safe direction to be wrong in.
 */
export function redactPathSegments( text: string ): string {

	return text.replace(
		new RegExp( '(' + API_NAMESPACE + '[^/?\\s"\'`]+)/[^\\s?"\'`]*', 'g' ),
		'$1/[redacted]',
	);
}

/**
 * Keys whose value is an identifier wherever it turns up in a payload. Used as
 * Bugsnag's `redactedKeys` - which covers breadcrumb metadata and the request
 * object as well as event metadata - and as the key test in redactSensitive().
 *
 * The street-level address fields are here for a narrower reason than the rest.
 * A route on its own is ordinary reporting data, and we want it: knowing a move
 * failed Amsterdam -> Berlin is how you reproduce it. A door number attached to
 * that route is not - it points at a household. So `city`, `country` and `state`
 * stay and `address`, `postcode` and `_houseNumber` go, which keeps the route
 * readable without carrying where anyone actually lives.
 *
 * Deliberately unanchored, unlike the rest of this array. `address` alone
 * would miss `formattedAddress` and `addressComponents` - the keys Google
 * Places actually logs - which is where the street address really leaks.
 *
 * That is safe from the same group-collapse hazard `nameEmailPhone` shows
 * for `/email/i`/`/phone/i` - but only in Bugsnag's own metadata walker,
 * which replaces a whole matching subtree with `[REDACTED]`. redactSensitive()
 * below works differently: JSON_FIELD matches only scalar values, so an
 * object-valued key is stepped over and its children matched individually.
 * Either way, no key in `mainForm` contains "address" or "postcode" as a
 * substring, so `movingFrom`/`movingTo` are never at risk from either
 * consumer. The one extra match is `movingFrom._initiallyFocusAddress`, a
 * boolean flag whose redaction costs nothing.
 *
 * `name` (DEV-7397) is anchored for the same reason `address`/`postcode` are
 * not: the plain substring would also catch `_customerName` - the
 * affiliate's company name (form.service.ts:245), not a person. It only
 * needed adding for redactSensitive() - Bugsnag's own walker already fully
 * redacted `nameEmailPhone` via the group-collapse above, with or without
 * this pattern.
 *
 * Every pattern is a key-name test over whatever a payload actually has, not
 * a lookup - a missing or renamed field (e.g. `name` -> `naam`) just never
 * matches, it never throws.
 */
export const REDACTED_KEYS = [
	/token/i, /^id$/i, /email/i, /phone/i, /auth_key/i,
	/address/i, /postcode/i, /houseNumber/i, /^name$/i,
];

/** A JSON `"key": value` pair, value quoted or bare (number, boolean, null). */
const JSON_FIELD = /"([^"]+)"\s*:\s*("(?:[^"\\]|\\.)*"|-?\d+(?:\.\d+)?|true|false|null)/g;

/**
 * Everything redactQueryValues() and redactPathSegments() do, plus the JSON shape:
 * a response body quoted into a log line carries its identifiers as fields rather
 * than as query parameters, e.g. {"token":"<capability token>"}. The key test is
 * REDACTED_KEYS itself rather than a second hand-written list, so the two cannot
 * drift - `id` is the name the three OTP routes actually use.
 */
export function redactSensitive( text: string ): string {

	return redactQueryValues( redactPathSegments( text ) ).replace(
		JSON_FIELD,
		( match, key: string ) =>
			REDACTED_KEYS.some( pattern => pattern.test( key ) ) ? '"' + key + '":"[redacted]"' : match,
	);
}

/**
 * DEV-7386: sanitising our own console calls is not enough on its own. Bugsnag's
 * browser notifier leaves a breadcrumb of its own for every XHR and fetch, and it
 * records the full request URL there - so `verify-otp?id=<capability token>` is
 * captured on a *successful* request, with no logging of ours involved. Console
 * output goes the same way, as `{ '[0]': ..., '[1]': ... }`.
 *
 * Scrub every string value on the way out, which covers both in one place and
 * covers call sites added later. Not every value is a string, and not every one
 * is scalar: an XHR breadcrumb's `status` is a number, and a History breadcrumb
 * carries `state` / `prevState` as raw objects. So the typeof guard below is
 * doing real work rather than being belt-and-braces, and anything it skips is
 * left to `redactedKeys`, which matches by key name at any depth.
 *
 * Not covered here: `event.request.url`. That is an event field rather than a
 * breadcrumb, so onBreadcrumb never sees it - sanitiseEventRequest() has it.
 */
export function sanitiseBreadcrumb( breadcrumb: { message?: string; metadata?: Record<string, unknown> } ): void {

	if( typeof breadcrumb.message === 'string' ) {

		breadcrumb.message = redactSensitive( breadcrumb.message );
	}

	const metadata = breadcrumb.metadata;

	if( ! metadata ) {

		return;
	}

	Object.keys( metadata ).forEach( key => {

		const value = metadata[key];

		if( typeof value === 'string' ) {

			metadata[key] = redactSensitive( value );
		}
	} );
}

/**
 * The one channel onBreadcrumb cannot reach. The notifier stamps
 * `event.request.url = window.location.href` onto every report, which is an event
 * field rather than a breadcrumb; `redactedKeys` does not cover it either, because
 * it matches key names and the key here is `url`. The form reads its identifiers
 * straight out of that URL - `?vc=` (form.service.ts:574) and `?customer=` (:586).
 *
 * This blanks every query value, not only those two: campaign and locale context
 * goes with them, which is the price of not having to enumerate what is secret.
 */
export function sanitiseEventRequest( event: { request?: { url?: string } } ): void {

	if( event.request && typeof event.request.url === 'string' ) {

		event.request.url = redactSensitive( event.request.url );
	}
}
